import React, { useEffect, useRef, useState } from 'react';
import pluralize from 'pluralize';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import {
  APIfetchMemory,
  APIshareMemory,
} from '../api/Memory';
import {
  APIfetchTimeline,
  APIshareTimeline,
} from '../api/Timeline';

import { useStore } from '../store';

import LoadingSmall from './LoadingSmall';

import type { Memory } from '../types/memory';
import type { Timeline } from '../types/timeline';
import type { ShareFormData } from '../types/forms/shareform';

import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

const ShareModal: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [errors, setErrors] = useState<any>({});
  const [success, setSuccess] = useState<boolean>(false);
  const [memory, setMemory] = useState<Memory | null>(null);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const params = useStore((state) => state.shareModal);
  const profile = useStore((state) => state.profile);
  const nav = useNavigate();

  const phoneFieldRef = useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (params.memoryId) {
          const memoryData = await APIfetchMemory(params.memoryId);
          setMemory(memoryData);
        }
        if (params.timelineId) {
          const timelineData = await APIfetchTimeline(params.timelineId);
          setTimeline(timelineData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [params.memoryId, params.timelineId]);

  const { mutate: shareMemory, isPending: isMemoryPending } = useMutation({
    mutationFn: async (data: ShareFormData) => {
      if (!memory) {
        throw new Error('No memory selected for sharing.');
      }
      return await APIshareMemory(memory.id, { timeline_id: data.timeline_id, email: data.email, name: data.name, phone: data.phone, comment: data.comment });
    },
    onError: (error) => {
      setErrors({
        email: error.message || 'An error occurred.',
      });
    },
    onSuccess: () => {
      setErrors({});
      setName('');
      setEmail('');
      setPhone('');
      setComment('');
      setMemory({
        ...memory,
        available_shares: memory ? memory.available_shares - 1 : 0,
      } as Memory);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    },
  });

  const { mutate: shareTimeline, isPending: isTimelinePending } = useMutation({
    mutationFn: async (data: ShareFormData) => {
      if (!timeline) {
        throw new Error('No timeline selected for sharing.');
      }
      return await APIshareTimeline(timeline.id, { email: data.email, name: data.name, phone: data.phone, comment: data.comment });
    },
    onError: (error) => {
      setErrors({
        email: error.message || 'An error occurred.',
      });
    },
    onSuccess: () => {
      setErrors({});
      setName('');
      setEmail('');
      setPhone('');
      setComment('');
      setTimeline({
        ...timeline,
        available_shares: timeline ? timeline.available_shares - 1 : 0,
      } as Timeline);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    },
  });

  const isPending = isMemoryPending || isTimelinePending;

  const handleChange = (event: React.FormEvent<HTMLFormElement>) => {
    const { name, value } = event.target as HTMLInputElement;
    if (name === 'email') {
      setEmail(value);
    }
    if (name === 'name') {
      setName(value);
    }
    if (name === 'phone') {
      setPhone(value);
    }
    if (name === 'comment') {
      setComment(value);
    }
  };

  const handleSubmit = () => {
    setErrors({});

    if (!email && !phone) {
      setErrors({
        email: 'Email or phone is required',
        phone: 'Email or phone is required',
      });
      return;
    }

    if (phone && phoneFieldRef.current) {
      const iti = phoneFieldRef.current.getInstance();
      if (!iti.isValidNumber()) {
        setErrors({
          phone: 'Invalid phone number format',
        });
        return;
      }
    }

    if (params.memoryId && memory) {
      shareMemory({ timeline_id: memory.timeline_id, email, name, phone, comment });
    } else if (params.timelineId && timeline) {
      shareTimeline({ email, name, phone, comment });
    }
  };

  if (!(params.memoryId || params.timelineId)) {
    return null;
  }

  const getLabel = () => {
    if (params.memoryId) {
      if (memory) {
        if (memory.variant === 'memory') {
          return 'Memory';
        } else {
          return 'SPRK';
        }
      }
    }
    if (params.timelineId) {
      return 'Timeline';
    }
    return '';
  };

  const getTitle = () => {
    if (params.memoryId && memory) {
      return memory.title || '';
    }
    if (params.timelineId && timeline) {
      return timeline.dedicated_to || '';
    }
    return '';
  };

  const getAvailableShares = () => {
    let availableShares = 0;
    if (params.memoryId && memory) {
      availableShares = memory.available_shares;
    }
    if (params.timelineId && timeline) {
      availableShares = timeline.available_shares;
    }
    return availableShares;
  };

  const buttonRedirect = (path: string) => {
    nav({ to: path, search: { redirect: window.location.pathname } });
    const dialog = document.querySelector<HTMLElement & { open: boolean }>('#share-modal');
    if (dialog) {
      dialog.open = false;
    }
  };

  return (
    <wa-dialog label={`Share ${getLabel()}`} id="share-modal">
      {!profile?.id ? (
        <div className="share-modal-login">
          <p className="center">
            Please log in or sign up to share this {getLabel().toLowerCase()}.
          </p>
          <div className="wa-cluster">
            <wa-button size="s" appearance="accent" onClick={() => buttonRedirect('/login')}>
              Log in
            </wa-button>
            <wa-button size="s" appearance="outlined" onClick={() => buttonRedirect('/signup')}>
              Sign up
            </wa-button>
          </div>
        </div>
      ) : (
        <>
          {loading ? (
            <LoadingSmall />
          ) : (
            <>
              {getTitle() && (
                <h3>{getTitle()}</h3>
              )}
              <p>
                You have <strong>{pluralize('email', getAvailableShares(), true)}</strong> left.
              </p>
              {errors.length > 0 && (
                <wa-callout variant="danger">
                  <i className="fa-regular fa-circle-exclamation" slot="icon"></i>
                  <strong>{errors[0]}</strong>
                </wa-callout>
              )}
              {success && (
                <wa-callout variant="success">
                  <i className="fa-regular fa-circle-check" slot="icon"></i>
                  <strong>{getLabel()} shared successfully!</strong>
                  <p>The {getLabel()} has been shared successfully.</p>
                </wa-callout>
              )}
              <form onSubmit={handleSubmit} onChange={handleChange}>
                  <wa-input type="email" name="email" label="Email" placeholder="Enter email" value={email} className={`${errors.email ? 'invalid' : ''}`} autofocus hint={errors.email}></wa-input>
                  <wa-input name="name" label="Name" placeholder="Enter name" value={name} className={`${errors.name ? 'invalid' : ''}`} hint={errors.name}></wa-input>
                  <wa-textarea name="comment" label="Comment" placeholder="Add a comment..." value={comment}></wa-textarea>
              </form>
              <wa-button onClick={handleSubmit} type="submit" appearance="accent" slot="footer" variant="brand" data-dialog="send" disabled={isPending || getAvailableShares() <= 0}>
                {isPending ? 'Sending...' : 'Send'}
              </wa-button>
            </>
          )}
        </>
      )}
    </wa-dialog>
  );
}

export default ShareModal;
